// DocBrowserPage.jsx — M2. Master-detail document browser over the doc-serve
// endpoints:
//   GET /api/project-groups        → resolve the default group slug (groups[0]).
//   GET /api/docs/tree?group&lang   → collapsible authorized tree (left).
//   GET /api/docs/content?group&docKey&lang → rendered HTML (right), with the B17
//     three-state presentation: 200 ok / 403 exists-but-forbidden / 404 not-found.
//
// Auth is server-side (viewer = session job post); nodes arrive pre-filtered with
// viewable/hasContent flags. Non-viewable nodes render greyed and, on click, show
// an inline "no permission" notice without a network round-trip.
//
// Routing: the URL is the source of truth for "which document is open"
// (`/docs/:group/*`, a splat — a docKey is a multi-segment logical path). The
// publish pipeline bakes exactly that route into every in-document link, so one
// mechanism serves doc-to-doc navigation, browser Back/Forward, the OQ-B17 return
// button and shareable deep links. The backend HTML is rendered as-is via
// dangerouslySetInnerHTML (escaped/sanitized server-side); anchor clicks are routed
// by shape: #anchor → default, /api/docs/asset → default (authorized asset),
// http(s) → new tab, /docs/... → in-SPA navigate, folder/broken → a notice.
//
// `?q=词一 词二`（第一百八十批）：来路带的关键字在正文里包成 <mark>，无 #hash 时滚到
// 首个命中；有 #hash 时锚点先行。纯逻辑在 docHighlight.mjs（有单测）。
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ancestorKeys, docHref, isAssetHref, parseDocHref, safeDecode } from './docRoute.mjs';
import { highlightTermsFromSearch, shouldScrollToHit, syncHighlights } from './docHighlight.mjs';
import {
  collectFolderKeys, contentLangFallback, docStalenessKey, emptyTreeReasonKey, findFirstDoc,
  findNode, freshnessDetail, zhOnlyNoticeKey,
} from './docTree.mjs';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import api, { ApiError } from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import { useAuth } from '../auth/AuthContext.jsx';
import AppSnackbar, { useNotify } from '../components/AppSnackbar.jsx';
import AssetLightbox from '../components/AssetLightbox.jsx';
import DocHistoryDrawer from '../components/DocHistoryDrawer.jsx';
import HelpLink from '../components/HelpLink.jsx';
import { fmtTime } from '../components/TicketDetailView.jsx';
import { alphaHintSx, downloadNameOf, enhancePlan, kindFromClass, wantsDownloadAttr } from './assetView.mjs';
import { DOC_HEADER_META_PROPS, docHeaderFlags, fmtUpdatedAt } from './docHistory.mjs';
import { useLang, useT } from '../i18n/index.mjs';

const OTHER_LANG = { zh: 'en', en: 'zh' };

// Transparency-hint preference: default ON (the whole point is that a designer
// should not have to remember to switch it on to notice alpha). Same storage
// convention as the language toggle (`dc_lang`).
const HINT_KEY = 'dc_alpha_hint';
function readHintPref() {
  try { return window.localStorage.getItem(HINT_KEY) !== '0'; } catch { return true; }
}

// ── caret glyph (no @mui/icons-material dependency in this project) ───────────
function Caret({ open }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        width: 20,
        justifyContent: 'center',
        fontSize: 12,
        lineHeight: 1,
        color: 'text.secondary',
        transition: 'transform 120ms',
        transform: open ? 'rotate(90deg)' : 'none',
        userSelect: 'none',
      }}
      aria-hidden
    >
      ▶
    </Box>
  );
}

// ── one tree row + its (collapsible) children ────────────────────────────────
function TreeNode({ node, depth, expanded, onToggle, onSelect, selectedKey, t }) {
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;
  const isOpen = expanded.has(node.docKey);
  const selectable = node.isDoc && node.viewable && node.hasContent;
  const denied = node.isDoc && !node.viewable;
  const selected = selectable && node.docKey === selectedKey;

  const onRowClick = () => {
    // Folder-only rows toggle; doc rows select (or surface a denied/empty notice).
    if (node.isDoc) onSelect(node);
    else if (hasChildren) onToggle(node.docKey);
  };

  return (
    <>
      <ListItemButton
        dense
        selected={selected}
        onClick={onRowClick}
        sx={{ pl: 1 + depth * 1.75, py: 0.5, opacity: denied ? 0.5 : 1, alignItems: 'center' }}
      >
        {hasChildren ? (
          <Box
            component="span"
            onClick={(e) => {
              e.stopPropagation();
              onToggle(node.docKey);
            }}
            sx={{ display: 'inline-flex', cursor: 'pointer' }}
          >
            <Caret open={isOpen} />
          </Box>
        ) : (
          <Box component="span" sx={{ width: 20, display: 'inline-block' }} />
        )}
        <ListItemText
          primary={node.title || node.docKey}
          // primaryTypographyProps was removed in @mui/material v9 — slotProps
          // is the live API (review batch 48; the old prop was a silent no-op).
          slotProps={{
            primary: {
              variant: 'body2',
              // S7（第五十六批）：原来是 noWrap + 原生 title= 提示。触屏根本打不开
              // title=（没有 hover），而第 4 层节点的标签只剩约 274px——手机上一个
              // 被截断的文档名就是**永远读不到**。故窄屏放到 3 行再截（≈60 字符，
              // 覆盖全库标题），宽屏仍是单行省略号，桌面观感不变；title= 留给鼠标。
              title: node.title || node.docKey,
              sx: {
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: { xs: 3, md: 1 },
                overflow: 'hidden',
                wordBreak: 'break-word',
              },
              fontWeight: node.isDoc ? 400 : 600,
              // SB-049 (OQ-A16): stub docs read greyed but stay clickable.
              color: node.status === 'stub'
                ? 'text.disabled'
                : node.isDoc ? 'text.primary' : 'text.secondary',
            },
          }}
        />
        {node.status === 'stub' ? (
          <Chip
            size="small"
            variant="outlined"
            label={t('docs.stubBadge')}
            sx={{ ml: 0.5, height: 18, fontSize: 11, color: 'text.disabled' }}
          />
        ) : null}
      </ListItemButton>
      {hasChildren ? (
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List disablePadding>
            {node.children.map((child) => (
              <TreeNode
                key={child.docKey}
                node={child}
                depth={depth + 1}
                expanded={expanded}
                onToggle={onToggle}
                onSelect={onSelect}
                selectedKey={selectedKey}
                t={t}
              />
            ))}
          </List>
        </Collapse>
      ) : null}
    </>
  );
}

export default function DocBrowserPage() {
  const t = useT();
  const { lang, setLang } = useLang();
  const { currentGroup, groups, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // URL IS the source of truth for "which document is open" (route /docs/:group/*).
  // That single choice buys doc-to-doc navigation, browser Back/Forward, the OQ-B17
  // return button and shareable deep links from ONE mechanism — and the route string
  // is already what publish.mjs bakes into every in-document link.
  const { group: routeGroup, '*': routeSplat } = useParams();
  const routeDocKey = (routeSplat || '').replace(/\/+$/, '') || null;
  const { hash, search } = useLocation();
  // 词表按 search 串记忆：它是 ContentPane 那条注入后增强 effect 的依赖，每次渲染
  // 新造一个数组会让那条 effect 在每次无关重渲染时都重跑。
  const hlTerms = useMemo(() => highlightTermsFromSearch(search), [search]);

  const [tree, setTree] = useState({
    status: 'loading', nodes: [], error: '', contentState: '',
  });
  const [expanded, setExpanded] = useState(() => new Set());
  const [content, setContent] = useState({ status: 'idle' }); // idle|loading|ok|forbidden|notfound|langUnavailable|error
  const { snack, notify, clearSnack } = useNotify();
  const [treeReload, setTreeReload] = useState(0); // bump to force a tree refetch
  const [histOpen, setHistOpen] = useState(false); // 第五十五批：修改历史抽屉
  // ── 第一百零三批：可见职位（audience）───────────────────────────────────────
  // null = 不渲染（加载中/失败/无文档）。它是辅助信息：加载失败**静默省略**，
  // 绝不把一篇已经打开的文档页打红。本页没有文件夹选中态（文件夹行只做展开/
  // 折叠，不进 URL），所以只做文档级——文件夹的 audience 端点语义已就绪，
  // 等哪天树长出文件夹选中交互再接。
  const [audience, setAudience] = useState(null);
  const didAutoExpand = useRef(false); // A4: run the initial auto-expand once per group

  // ── 第五十六批 S4：窄屏的主从折叠 ────────────────────────────────────────
  // 病灶：xs 下布局是 column、目录 Paper 是 width:100% 且 maxHeight 只在 md 生效，
  // 于是**整棵展开的树**顶在正文上方；「全部展开」后能有好几屏。点一个节点只会
  // 导航，不会把正文滚进视口——每次跳转都得先划过整棵树。
  // 解法选型（三选一）：抽屉 / 滚动到正文 / 可折叠区段。选**可折叠区段**：
  //   · 本页已经有一个抽屉（修改历史），再来一个右侧抽屉会让"抽屉"这个词失去指向；
  //   · 目录不是临时动作而是常驻导航，折叠区段能保留"我在树的哪一处"的上下文；
  //   · 它是纯布局，md 以上把 Collapse 恒开即可，桌面渲染一字未变。
  // 另加两条：展开时也给 xs 一个 maxHeight（树在自己的容器里滚，不再顶正文），
  // 以及换文档后把正文滚进视口。
  // 判据是运行时视口宽度而非 CSS：这里要改的是**行为**（选中后收起目录、滚动），
  // 那是 CSS 表达不了的。
  const narrow = useMediaQuery((th) => th.breakpoints.down('md'), { noSsr: true });
  const [treeOpen, setTreeOpen] = useState(() => !routeDocKey);
  const contentRef = useRef(null);

  // A deep link names its own group; a bare /docs follows the session's current
  // group (第一百零一批切换器联动)。
  // ── 第一百零三批：组上下文未解析前 groupSlug 为 null——渲染 loading、不发请求 ──
  // 此前这里回落到硬编码的 MirrorMii slug 就开始请求树：只属新组的成员会先看到
  // 一个 404「unknown or unpublished group」面板，等 /api/me 回来才自愈成自己的
  // 组。那个 404 说的不是真的——上下文未就绪的真实状态是 loading，不是猜一个组。
  const groupSlug = routeGroup || currentGroup?.slug || null;
  // 「还在等 /api/me」与「等到了、但这个账号一个组都没有」是两个状态（对抗审查
  // 坐实）：零成员账号在旧写法下 groupSlug 永远是 null，loading 转一辈子。
  // 后者要给结论，不是给转圈。
  const noGroupAtAll = !authLoading && !routeGroup && (groups?.length ?? 0) === 0;

  // (Re)load the tree whenever the group slug or language changes.
  useEffect(() => {
    let alive = true;
    if (noGroupAtAll) {
      setTree({ status: 'error', nodes: [], error: t('docs.tree.noGroup'), contentState: '' });
      return undefined;
    }
    setTree((s) => ({ ...s, status: 'loading' }));
    if (!groupSlug) return undefined;   // 组上下文未就绪：保持 loading，别拿假组去问
    (async () => {
      try {
        const data = await api.docsTree(groupSlug, lang);
        if (!alive) return;
        // contentState 原样收下（值域判断在 docTree.mjs::emptyTreeReasonKey，
        // 未知值回退到最弱的那句真话）——服务端从本批起区分「还没发布过」与
        // 「发布过但你看不到」，不读它等于那次区分没有发生。
        setTree({
          status: 'ready',
          nodes: Array.isArray(data?.tree) ? data.tree : [],
          error: '',
          contentState: typeof data?.contentState === 'string' ? data.contentState : '',
        });
      } catch (e) {
        if (!alive) return;
        setTree({
          status: 'error', nodes: [], error: errMessage(e, t, 'docs.tree.error'), contentState: '',
        });
      }
    })();
    return () => {
      alive = false;
    };
  }, [groupSlug, noGroupAtAll, lang, t, treeReload]);

  // Load content BY docKey. The server is the authority (GET /api/docs/content
  // classifies a bare docKey itself: 200 / 403+title / 404), so a link target needs
  // no tree node — which matters because B17-concealed docs are PRUNED from the tree
  // yet remain legal link targets. The tree node is used only as a hint to tell
  // "this language isn't published" apart from "absent or concealed" on a 404.
  const loadContent = useCallback(
    async (docKey, curLang) => {
      // groupSlug 为 null 只可能在裸 /docs（深链自带组段）——那时 docKey 也为
      // null；仍然双条件判：拿 null 组去问 content 得到的 404 同样说的不是真的。
      if (!docKey || !groupSlug) {
        setContent({ status: 'idle' });
        return;
      }
      const hint = findNode(tree.nodes, docKey);
      setContent({ status: 'loading' });
      // 成功应答 → 本页 state 的唯一映射（回落取到另一侧语言时也走它，两处不许各抄一份）。
      const okState = (data) => ({
        status: 'ok',
        title: data.title,
        html: data.html,
        availableLangs: Array.isArray(data.availableLangs) ? data.availableLangs : [],
        // 第五十五批 §4.1：更新时间来自 manifest 的逐文档字段，**不受追踪开关门控**
        // （它是单一事实、不构成日志）；未提交过的文件两字段为 null，须容忍。
        updatedAtMs: Number.isFinite(Number(data.updatedAtMs)) && data.updatedAtMs ? Number(data.updatedAtMs) : null,
        updatedRef: data.updatedRef || null,
        // 历史入口只认真正的 true——字段缺失（旧后端）时不该点亮一个必然 404 的按钮。
        historyEnabled: data.historyEnabled === true,
        // 处置轮 R15：这两位**原样收下**（值域与 null 语义的判断全在
        // docTree.mjs::docStalenessKey / freshnessDetail 里）。它们此前落地了而
        // 没有任何一处读——而 routes_docs.cpp 给这一段写的理由正是「投影落在文档面
        // 上是**承重的**」。落地之后没人读，在产品面上与没落一模一样。
        contentState: typeof data?.contentState === 'string' ? data.contentState : '',
        freshness: data?.freshness ?? null,
      });
      try {
        const data = await api.docContent(groupSlug, docKey, curLang);
        setContent(okState(data));
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) {
          // B17: exists but forbidden — the body discloses existence + title only.
          setContent({ status: 'forbidden', title: e.body?.title || hint?.title || '' });
        } else if (e instanceof ApiError && e.status === 404) {
          // 2026-08-18 外来单语组（SOP-14 §7）：只有 zh 页的文档在 en 界面下，
          // content 端点是 404「not published in this language」。**最小诚实**处理：
          // 再问一次另一侧语言——命中就给真实存在的那一页（正文上方有单语提示条，
          // 见 zhOnlyNoticeKey），绝不造假 en 页。两侧都 404 才落回原来的归类；
          // 403 不走这条（可见性按 docKey 判，与语言无关）。这条不依赖树 hint，
          // 深链先于树加载到达时同样成立。
          try {
            const data = await api.docContent(groupSlug, docKey, contentLangFallback(curLang));
            setContent(okState(data));
            return;
          } catch { /* 回落也取不到 ⇒ 按原 404 语义归类 */ }
          if (hint?.viewable && !hint?.hasContent) {
            // In the tree and viewable, but this language has no artifact ⇒ offer a switch.
            setContent({ status: 'langUnavailable', title: hint.title });
          } else {
            // Absent OR B17-concealed — deliberately indistinguishable.
            setContent({ status: 'notfound', title: hint?.title || '' });
          }
        } else {
          setContent({ status: 'error', error: errMessage(e, t, 'docs.content.error') });
        }
      }
    },
    [groupSlug, t, tree.nodes],
  );

  // The URL drives content: navigating (link click, tree click, Back/Forward, a
  // pasted deep link) re-runs this. Language changes re-run it too.
  useEffect(() => {
    loadContent(routeDocKey, lang);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeDocKey, lang, groupSlug]);

  // 换文档就收起历史抽屉：留着不关会让读者以为看的是新文档的历史（抽屉内部虽会
  // 重取，但那一瞬间显示的仍是上一篇的列表）。
  useEffect(() => { setHistOpen(false); }, [routeDocKey, groupSlug]);

  // 第一百零三批：文档成功打开后才问观众。403/404 面本身就是「你看不见」，而
  // audience 端点对看不见的调用者一律 404 隐匿——那时问了也是白问。换文档先清
  // 空再取，避免把上一篇的名单挂在新标题下的那一瞬间。
  useEffect(() => {
    let alive = true;
    setAudience(null);
    if (!routeDocKey || !groupSlug || content.status !== 'ok') return undefined;
    (async () => {
      try {
        const data = await api.docAudience(groupSlug, routeDocKey);
        if (alive) setAudience(Array.isArray(data?.audience) ? data.audience : null);
      } catch {
        if (alive) setAudience(null);   // 辅助信息：失败静默省略，不打红文档页
      }
    })();
    return () => { alive = false; };
  }, [routeDocKey, groupSlug, content.status]);

  const onToggle = useCallback((docKey) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(docKey)) next.delete(docKey);
      else next.add(docKey);
      return next;
    });
  }, []);

  // A3: expand-all / collapse-all over the in-memory tree (no network).
  const expandAll = useCallback(() => {
    setExpanded(new Set(collectFolderKeys(tree.nodes)));
  }, [tree.nodes]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  // A4: a fresh group resets the once-per-group auto-expand.
  useEffect(() => {
    didAutoExpand.current = false;
  }, [groupSlug]);

  // A4: on the first successful tree load, reveal (only) the first doc in reading
  // order — expand its ancestor folders, collapse the rest — so it is directly
  // clickable. Reveal-only (no auto-select) to avoid a surprise content fetch;
  // runs once per group so it never fights a later manual collapse / lang switch.
  useEffect(() => {
    if (routeDocKey) return;                      // a deep link reveals its own target
    if (tree.status !== 'ready' || tree.nodes.length === 0 || didAutoExpand.current) return;
    didAutoExpand.current = true;
    const first = findFirstDoc(tree.nodes);
    if (first && first.ancestors.length > 0) setExpanded(new Set(first.ancestors));
  }, [tree, routeDocKey]);

  // Scroll to a #fragment once the document body is actually in the DOM (the HTML is
  // injected asynchronously, so the browser's native jump has nothing to hit). Heading
  // ids are slugs of the LOCALIZED heading (md-render slugify), so a fragment written
  // in the other language will not resolve — degrade silently rather than jumping wrong.
  useEffect(() => {
    if (content.status !== 'ok' || !hash) return;
    const id = safeDecode(hash.slice(1));
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ block: 'start' });
  }, [content.status, content.html, hash]);

  // Reveal the routed document: union its ancestor folders into `expanded` (never
  // replace — a jump must not collapse what the reader already opened). Folder
  // docKeys ARE the '/'-prefixes of a docKey, so this needs no tree traversal.
  useEffect(() => {
    if (!routeDocKey) return;
    didAutoExpand.current = true;                 // suppress the A4 landing expansion
    const anc = ancestorKeys(routeDocKey);
    if (anc.length === 0) return;
    setExpanded((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const k of anc) if (!next.has(k)) { next.add(k); changed = true; }
      return changed ? next : prev;
    });
  }, [routeDocKey]);

  // Selecting a tree row NAVIGATES (it does not set local state) — so a tree click
  // and an in-document link click take the exact same path, and both are shareable.
  const onSelect = useCallback(
    (node) => navigate(docHref(groupSlug, node.docKey)),
    [navigate, groupSlug],
  );

  // S4：打开某篇文档就收起目录，回到无文档态则展开。挂在 routeDocKey 上（而不是
  // 写进 onSelect）是因为进入文档的路子有三条——点树、点正文里的站内链接、粘一个
  // 深链——三条都该落在正文上。md 以上 treeOpen 不参与渲染，故这条对桌面无影响。
  useEffect(() => { setTreeOpen(!routeDocKey); }, [routeDocKey]);

  // S4：窄屏换文档后把正文顶进视口。带 #fragment 的链接不碰——那由下面那条
  // 锚点滚动的 effect 负责，两个一起滚会互相打架（先跳到正文头再跳到锚点，闪一下）。
  useEffect(() => {
    if (!narrow || !routeDocKey || hash) return;
    contentRef.current?.scrollIntoView({ block: 'start' });
  }, [narrow, routeDocKey, hash]);

  // Anchor clicks inside the rendered HTML. The publish pipeline bakes real routes,
  // so a doc-link is navigated in-SPA; assets open normally; only genuinely
  // unsupported shapes (folder links) still fall back to the notice.
  const onContentClick = useCallback(
    (e) => {
      const a = e.target.closest && e.target.closest('a');
      if (!a) return;
      const href = a.getAttribute('href') || '';
      if (href.startsWith('#')) return;                    // same-page anchor
      if (isAssetHref(href)) return;                       // authorized asset — let it open
      e.preventDefault();
      if (/^https?:\/\//i.test(href)) {
        window.open(href, '_blank', 'noopener,noreferrer');
        return;
      }
      // publish.mjs marks unresolvable .md targets `doc-link broken` — say so plainly
      // instead of the generic "coming soon".
      if (a.classList && a.classList.contains('broken')) {
        notify(t('docs.link.broken'), 'warning');
        return;
      }
      const target = parseDocHref(href);
      if (target) {
        navigate(docHref(target.group, target.docKey) + (target.frag ? `#${target.frag}` : ''));
        return;
      }
      notify(t('docs.link.unsupported'), 'info');          // e.g. folder links (论点库/)
    },
    [navigate, notify, t],
  );

  // B5: "propose a change to THIS doc" — deep-link to the submit form with the
  // docKey+title+group prefilled (the browser already holds the canonical docKey).
  const onProposeChange = useCallback(() => {
    if (!routeDocKey) return;
    navigate('/submit', {
      state: { docKey: routeDocKey, title: content.title || routeDocKey, group: groupSlug },
    });
  }, [navigate, routeDocKey, content.title, groupSlug]);

  return (
    <Box>
      <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 2 }}>
        {t('docs.title')}
        {/* SB-151：section 必须与 HELP_PAGE_MAP 里本路由那一行一致（helpLinks.test.mjs 对表核对）。 */}
        <HelpLink section="docs" />
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: 'stretch' }}>
        {/* ── left: tree ─────────────────────────────────────────────────── */}
        <Paper
          variant="outlined"
          sx={{
            width: { xs: '100%', md: 340 },
            flexShrink: 0,
            // xs 也要有上限：展开的树必须在**自己的容器里**滚，而不是把正文推到
            // 好几屏之下（这正是 S4 的病灶；「全部展开」会让它更长）。
            maxHeight: { xs: '60vh', md: 'calc(100vh - 180px)' },
            overflow: 'auto',
            alignSelf: { md: 'flex-start' },
          }}
        >
          {/* 窄屏的折叠开关。md 以上整块不渲染（display:none），桌面一字未变。 */}
          <Box sx={{
            display: { xs: 'block', md: 'none' },
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            borderBottom: treeOpen ? '1px solid' : 0,
            borderColor: 'divider',
          }}
          >
            <Button
              fullWidth
              onClick={() => setTreeOpen((o) => !o)}
              aria-expanded={treeOpen}
              sx={{ justifyContent: 'flex-start', px: 1, py: 1 }}
            >
              <Caret open={treeOpen} />
              <Box component="span" sx={{ ml: 0.5 }}>
                {treeOpen ? t('docs.tree.hide') : t('docs.tree.show')}
              </Box>
            </Button>
          </Box>
          <Collapse in={!narrow || treeOpen} timeout="auto">
            {tree.status === 'loading' ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : tree.status === 'error' ? (
              <Box sx={{ p: 2 }}>
                <Alert severity="error" sx={{ mb: 1 }}>
                  {tree.error}
                </Alert>
                <Button size="small" onClick={() => setTreeReload((n) => n + 1)}>
                  {t('common.retry')}
                </Button>
              </Box>
            ) : tree.nodes.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" data-dc-empty-tree>
                  {t(emptyTreeReasonKey(tree.contentState))}
                </Typography>
              </Box>
            ) : (
              <>
                <Stack
                  direction="row"
                  spacing={0.5}
                  sx={{
                    px: 1,
                    py: 0.5,
                    position: 'sticky',
                    // 窄屏上折叠开关自己也是 sticky（高约 40px），全展/全折这一行
                    // 必须让开它，否则两条 sticky 会叠在一起互相盖住。
                    top: { xs: 40, md: 0 },
                    bgcolor: 'background.paper',
                    zIndex: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Button size="small" onClick={expandAll} sx={{ minWidth: 0, fontSize: 12 }}>
                    {t('docs.tree.expandAll')}
                  </Button>
                  <Button size="small" onClick={collapseAll} sx={{ minWidth: 0, fontSize: 12 }}>
                    {t('docs.tree.collapseAll')}
                  </Button>
                </Stack>
                <List disablePadding sx={{ py: 0.5 }}>
                  {tree.nodes.map((node) => (
                    <TreeNode
                      key={node.docKey}
                      node={node}
                      depth={0}
                      expanded={expanded}
                      onToggle={onToggle}
                      onSelect={onSelect}
                      selectedKey={routeDocKey}
                      t={t}
                    />
                  ))}
                </List>
              </>
            )}
          </Collapse>
        </Paper>

        {/* ── right: content ─────────────────────────────────────────────── */}
        {/* scrollMarginTop：AppShell 的 AppBar 是 position:sticky（xs 下 56px 高），
            scrollIntoView 把 Paper 顶到视口 0 会让正文头一截藏在它底下。 */}
        <Paper
          ref={contentRef}
          variant="outlined"
          sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, sm: 3 }, scrollMarginTop: { xs: 64, md: 0 } }}
        >
          <ContentPane
            content={content}
            audience={audience}
            highlightTerms={hlTerms}
            hash={hash}
            lang={lang}
            setLang={setLang}
            onContentClick={onContentClick}
            onRetry={() => loadContent(routeDocKey, lang)}
            onBack={() => navigate(-1)}
            onProposeChange={onProposeChange}
            onOpenHistory={() => setHistOpen(true)}
            t={t}
          />
        </Paper>
      </Box>

      <DocHistoryDrawer
        open={histOpen}
        group={groupSlug}
        docKey={routeDocKey}
        docTitle={content.title}
        onClose={() => setHistOpen(false)}
      />
      <AppSnackbar snack={snack} onClose={clearSnack} />
    </Box>
  );
}

// ── content pane: the B17 three-state + rendered HTML ────────────────────────
function ContentPane({
  content, audience, highlightTerms, hash, lang, setLang, onContentClick, onRetry,
  onProposeChange, onOpenHistory, onBack, t,
}) {
  const other = OTHER_LANG[lang] || 'en';
  // Batch 52 — asset presentation. The publish pipeline bakes `doc-asset
  // doc-asset--<kind>` onto every asset reference (scripts/publish/asset-kind.mjs);
  // everything below reads only that class, never the URL.
  const bodyRef = useRef(null);
  const [shot, setShot] = useState(null);          // {src, alt, kind} | null
  const [hint, setHint] = useState(() => readHintPref());

  const toggleHint = useCallback(() => {
    setHint((on) => {
      const next = !on;
      try { window.localStorage.setItem(HINT_KEY, next ? '1' : '0'); } catch { /* private mode */ }
      return next;
    });
  }, []);

  // Post-injection enhancement. React replaces the innerHTML wholesale on every
  // doc/lang switch, so this is keyed on the html string and must be idempotent
  // (it may re-run on the same DOM) — hence the data-dc-enhanced marker.
  useEffect(() => {
    const root = bodyRef.current;
    if (!root || content.status !== 'ok') return;
    for (const el of root.querySelectorAll('img.doc-asset, a.doc-asset')) {
      if (el.dataset.dcEnhanced) continue;
      el.dataset.dcEnhanced = '1';
      const kind = kindFromClass(el.getAttribute('class'));
      const plan = enhancePlan({
        tag: el.tagName, kind, inTable: Boolean(el.closest('td, th')),
      });
      if (wantsDownloadAttr({ tag: el.tagName, kind })) {
        // 写着「下载」的链接要真的下载（病灶与取舍见 assetView.mjs wantsDownloadAttr
        // 上方）：同源 download 属性，文件名取自 href 的 path 参数。
        el.setAttribute('download', downloadNameOf(el.getAttribute('href')));
      }
      if (plan.thumb && el.tagName === 'IMG' && el.parentNode) {
        // Uniform 1:1 letterboxed cell: the frame carries the aspect ratio, the
        // image keeps its own ratio inside it (object-fit: contain). Wrapping is
        // done here rather than in markdown so the source files stay clean.
        const frame = document.createElement('span');
        frame.className = 'dc-thumb';
        el.parentNode.insertBefore(frame, el);
        frame.appendChild(el);
      }
      if (plan.upgrade && el.parentNode) {
        // md-render deliberately emits no raw HTML, so audio/video arrive either
        // as a plain link (`[x](a.mp3)`) or as an image element (`![x](a.mp3)` —
        // a shape asset-kind.mjs explicitly anticipates, keying on the extension
        // rather than the markdown syntax). Batch 58 wrote the style board's audio
        // section with image syntax, and because this branch only handled `A`, the
        // seven licensed tracks rendered as broken <img> elements with a correct
        // `doc-asset--audio` class and no player anywhere. Handle both.
        const player = document.createElement(plan.upgrade);
        player.setAttribute('src', el.getAttribute('href') || el.getAttribute('src') || '');
        player.setAttribute('controls', '');
        player.setAttribute('preload', 'metadata');   // 别为一页七轨各拉一整个文件
        player.className = 'dc-player';
        if (plan.replace) {
          // <img> 形态：那个元素本身是坏的（浏览器在渲染一个不是图片的东西），
          // 留着只会显示碎图标——顶替掉，把 alt 挪成播放器的可访问名。
          const label = el.getAttribute('alt');
          if (label) player.setAttribute('aria-label', label);
          el.parentNode.replaceChild(player, el);
        } else {
          // <a> 形态：原链接是有价值的下载退路（仍走受权的资产端点），留着。
          el.parentNode.insertBefore(player, el.nextSibling);
        }
      }
    }
    // ── 关键字高亮（?q=）──────────────────────────────────────────────────
    // 放在资产增强**之后**：播放器/缩略图框是新造的元素，先包 mark 再挪节点会把
    // mark 留在被顶替掉的 <img> 旁边。幂等由 syncHighlights 自己保证（root 上记
    // 词表身份 + 看现场有没有 mark），所以本 effect 因 hash 变化重跑也不会重做。
    // 滚动归属：有 #hash 时让上面那条锚点 effect 先行（父组件的 effect 在子组件
    // 之后跑，两个都滚会先跳命中再跳锚点、闪一下）；无 hash 且本次真换了词表才滚。
    // block:'center' 而非 'start'：mark 是行内元素，居中天然避开 sticky AppBar。
    const hl = syncHighlights(root, highlightTerms || [], document);
    if (shouldScrollToHit({ hash, changed: hl.changed, count: hl.count })) {
      root.querySelector('mark.dc-hl')?.scrollIntoView({ block: 'center' });
    }
  }, [content.html, content.status, highlightTerms, hash]);

  const onBodyClick = useCallback((e) => {
    const img = e.target.closest && e.target.closest('img.doc-asset--image');
    if (img) {
      e.preventDefault();
      setShot({ src: img.getAttribute('src'), alt: img.getAttribute('alt'), kind: 'image' });
      return;
    }
    const vid = e.target.closest && e.target.closest('a.doc-asset--video');
    if (vid) {
      e.preventDefault();
      setShot({ src: vid.getAttribute('href'), alt: vid.textContent, kind: 'video' });
      return;
    }
    onContentClick(e);
  }, [onContentClick]);
  // OQ-B17 mandates a RETURN affordance on the "exists but no permission" pane; it
  // was un-implementable while nothing could navigate here. Now that in-document
  // links navigate, it applies to every dead-end pane.
  const back = (
    <Button size="small" onClick={onBack} sx={{ mt: 1 }}>
      {t('docs.content.back')}
    </Button>
  );

  if (content.status === 'idle') {
    return (
      <Typography variant="body1" color="text.secondary">
        {t('docs.content.placeholder')}
      </Typography>
    );
  }
  if (content.status === 'loading') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (content.status === 'forbidden') {
    return (
      <Alert severity="warning">
        <Typography sx={{ fontWeight: 700 }}>{t('docs.content.forbiddenTitle')}</Typography>
        {content.title ? (
          <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
            {content.title}
          </Typography>
        ) : null}
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t('docs.content.forbiddenDesc')}
        </Typography>
        {back}
      </Alert>
    );
  }
  if (content.status === 'notfound') {
    return (
      <Alert severity="info">
        <Typography sx={{ fontWeight: 700 }}>{t('docs.content.notFoundTitle')}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t('docs.content.notFoundDesc')}
        </Typography>
        {back}
      </Alert>
    );
  }
  // 到达这一支的前提是**另一侧语言的回落请求也已 404**（上方 catch 里先试过一次），
  // 所以这里绝不渲染「切换到…」按钮——那是一只结构上必败的按钮，点了换语言照样
  // 取不到（SB-151 证伪评审点名；手册第二章「另一侧真的存在时才有切换钮」由此成立）。
  if (content.status === 'langUnavailable') {
    return (
      <Alert severity="info">
        <Typography sx={{ fontWeight: 700 }}>{t('docs.content.langUnavailableTitle')}</Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {t('docs.content.langUnavailableDesc', { lang: lang.toUpperCase() })}
        </Typography>
      </Alert>
    );
  }
  if (content.status === 'error') {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 1 }}>
          {content.error}
        </Alert>
        <Button size="small" onClick={onRetry}>
          {t('common.retry')}
        </Button>
      </Box>
    );
  }

  // status === 'ok'
  const langs = content.availableLangs || [];
  const missingCurrent = langs.length > 0 && !langs.includes(lang);
  const flags = docHeaderFlags(content);
  // 处置轮 R15：过时横幅挂在**正文之前**。挂在后面等于没挂（正文可能有几百行，
  // 读者不会翻回来），而这一整条投影存在的理由就是「让读者带着过时预期开始读」。
  const staleKey = docStalenessKey(content.contentState);
  const behind = staleKey ? freshnessDetail(content.freshness) : null;
  return (
    <Box>
      {staleKey ? (
        <Alert severity="warning" sx={{ mb: 1.5 }} data-testid="doc-stale-banner">
          <Typography variant="body2">{t(staleKey)}</Typography>
          {behind ? (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t(behind.key, behind.hours === undefined ? undefined : { hours: behind.hours })}
            </Typography>
          ) : null}
        </Alert>
      ) : null}
      {/* 标题行整行也允许换行：窄屏上先让右栏自己折（见 DOC_HEADER_META_PROPS），
          仍然挤不下时整块落到标题下一行——横向滚动条不许出现在页面 body 上。 */}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap' }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 700, minWidth: 0 }}>
          {content.title}
        </Typography>
        {/* 布局在 docHistory.mjs（有单测）：这一栏**不得**带 flexShrink: 0，
            否则 flexWrap 是死的，右栏在 360px 视口下会把页面顶出横向滚动。 */}
        <Stack {...DOC_HEADER_META_PROPS}>
          {langs.length > 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {t('docs.content.availableLangs', { langs: langs.map((l) => l.toUpperCase()).join(', ') })}
            </Typography>
          ) : null}
          {/* 第五十五批 §5.2：更新时间。manifest 的逐文档字段，不受追踪开关门控；
              从未提交过的文件为 null，那时整块不渲染（宁可没有，也不显示"1970"）。
              两个可选块的判据在 docHistory.mjs（纯函数、有单测）。 */}
          {/* 行内只到日（fmtUpdatedAt），完整时分与短 sha 都进 tooltip：
              「更新于 2026年7月30日 20:03」单块就近 190px，是这一行挤爆窄屏的大头。 */}
          {flags.showUpdated ? (
            <Tooltip
              title={flags.updatedRef
                ? t('docs.content.updatedTip', {
                  time: fmtTime(flags.updatedAtMs, lang), ref: flags.updatedRef,
                })
                : fmtTime(flags.updatedAtMs, lang)}
            >
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                {t('docs.content.updatedAt', { time: fmtUpdatedAt(flags.updatedAtMs, lang) })}
              </Typography>
            </Tooltip>
          ) : null}
          {/* 历史入口仅在该项目组开了追踪时出现（§5.2）。 */}
          {flags.showHistory ? (
            <Button size="small" onClick={onOpenHistory} sx={{ whiteSpace: 'nowrap' }}>
              {t('docs.content.history')}
            </Button>
          ) : null}
          <Button size="small" variant="outlined" onClick={onProposeChange} sx={{ whiteSpace: 'nowrap' }}>
            {t('docs.content.proposeChange')}
          </Button>
        </Stack>
      </Stack>
      {/* ── 第一百零三批：可见职位 ─────────────────────────────────────────────
          需求方原话「向能看到文档和文件夹的账号曝光可实际看见此资源的职位列表，
          以方便互相沟通」。名单来自 /api/docs/audience（与真实鉴权同一只匹配器，
          只列 job posts）；audience=null（加载中/失败）整行不渲染——辅助信息不
          制造闪烁也不打红页面。空名单如实说「仅管理员可见」：不说的话，读者会
          以为这行坏了，而不是这篇文档真的没有职位观众。 */}
      {audience ? (
        <Stack
          direction="row"
          spacing={0.5}
          useFlexGap
          sx={{ alignItems: 'center', flexWrap: 'wrap', mb: 1 }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            {t('docs.audience.label')}
          </Typography>
          {audience.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              {t('docs.audience.none')}
            </Typography>
          ) : (
            audience.map((a) => (
              <Chip
                key={a.post}
                size="small"
                variant="outlined"
                label={lang === 'en' ? (a.postEn || a.post) : a.post}
                sx={{ height: 20, fontSize: 11 }}
              />
            ))
          )}
        </Stack>
      ) : null}
      {missingCurrent ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            langs.includes(other) ? (
              <Button color="inherit" size="small" onClick={() => setLang(other)}>
                {t('docs.content.switchTo', { lang: other.toUpperCase() })}
              </Button>
            ) : null
          }
        >
          {/* 2026-08-18 外来单语组：只有 zh 原文时说专句「此文档仅有中文原文」——
              正文就是回落取到的 zh 页（见 loadContent 的 404 回落）；其它缺语组合
              沿用通用句。判据在 docTree.mjs::zhOnlyNoticeKey（纯函数、有单测）。 */}
          {zhOnlyNoticeKey(lang, langs)
            ? t(zhOnlyNoticeKey(lang, langs))
            : t('docs.content.langUnavailableDesc', { lang: lang.toUpperCase() })}
        </Alert>
      ) : null}
      <Divider sx={{ mb: 2 }} />
      <Box
        ref={bodyRef}
        onClick={onBodyClick}
        className={hint ? 'dc-hint-on' : undefined}
        // Backend HTML is escaped/sanitized server-side (see doc-serve contract).
        dangerouslySetInnerHTML={{ __html: content.html || '' }}
        sx={(theme) => ({
          lineHeight: 1.7,
          fontSize: 15,
          wordBreak: 'break-word',
          '& h1': { fontSize: 26, fontWeight: 700, mt: 3, mb: 1.5 },
          '& h2': { fontSize: 22, fontWeight: 700, mt: 3, mb: 1.5 },
          '& h3': { fontSize: 18, fontWeight: 700, mt: 2.5, mb: 1 },
          '& h4, & h5, & h6': { fontWeight: 700, mt: 2, mb: 1 },
          // 锚点跳转（#标题 slug / #XX-nn 条目锚）落点让开 sticky AppBar：Toolbar
          // xs 56、sm 起 64，各加 16 呼吸位。scrollIntoView({block:'start'}) 会把目标
          // 顶到视口 0，没有这条标题就藏在工具条底下，读者看到的是下一段。
          // li[id]：发布态把 `**XX-nn …**` 开头的列表项渲染成 <li id="XX-nn">，
          // 条目锚与标题锚是同一个问题。
          '& h1, & h2, & h3, & h4, & h5, & h6, & li[id]': { scrollMarginTop: { xs: 72, sm: 80 } },
          '& p': { my: 1.25 },
          '& ul, & ol': { pl: 3, my: 1.25 },
          '& li': { my: 0.5 },
          '& a': { color: 'primary.main' },
          '& img': { maxWidth: '100%', height: 'auto' },
          '& code': {
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
            fontSize: '0.875em',
            bgcolor: 'action.hover',
            px: 0.5,
            py: 0.25,
            borderRadius: 1,
          },
          '& pre': {
            bgcolor: 'action.hover',
            p: 2,
            borderRadius: 1,
            overflowX: 'auto',
            my: 1.5,
          },
          '& pre code': { bgcolor: 'transparent', p: 0 },
          '& blockquote': {
            borderLeft: '4px solid',
            borderColor: 'divider',
            pl: 2,
            ml: 0,
            my: 1.5,
            color: 'text.secondary',
          },
          '& table': { borderCollapse: 'collapse', display: 'block', overflowX: 'auto', my: 1.5 },
          '& th, & td': { border: '1px solid', borderColor: 'divider', px: 1.5, py: 0.75 },
          '& th': { bgcolor: 'action.hover', fontWeight: 700 },
          '& hr': { border: 0, borderTop: '1px solid', borderColor: 'divider', my: 3 },
          // ?q= 关键字高亮（docHighlight.mjs 包的 mark）。color:inherit——浏览器默认
          // 给 <mark> 配黑字，深色主题下黄底黑字在一段浅字里像一块补丁。
          '& mark.dc-hl': {
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 213, 79, 0.35)' : 'rgba(255, 235, 59, 0.6)',
            color: 'inherit',
            borderRadius: 0.5,
            px: 0.25,
          },

          // ── batch 52: uniform 1:1 thumbnails inside tables ──────────────────
          // Cells used to be shoved around by whatever pixel size each image
          // happened to have; the frame fixes the cell geometry, the image keeps
          // its own ratio inside it.
          '& .dc-thumb': {
            display: 'inline-flex',
            width: 180,
            maxWidth: '100%',
            aspectRatio: '1 / 1',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          },
          '& .dc-thumb img': {
            maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto',
            objectFit: 'contain',
          },
          '& img.doc-asset--image': { cursor: 'zoom-in' },
          '& .dc-player': { display: 'block', maxWidth: '100%', my: 1 },
          '& audio.dc-player': { width: '100%' },

          // ── dynamic transparent-area hint ────────────────────────────────────
          // The stripe pattern itself now lives in assetView.mjs (alphaHintSx) so
          // the lightbox can paint the very same thing on its own <img>: that image
          // renders into the Dialog portal and is NOT a descendant of .dc-hint-on,
          // which is why its hint button used to do nothing at all (D-B). Only the
          // selector differs between the two mount points — never the paint.
          ...alphaHintSx(theme.palette.mode, '&.dc-hint-on img.doc-asset--image'),
        })}
      />
      <AssetLightbox
        open={Boolean(shot)}
        src={shot?.src || ''}
        alt={shot?.alt || ''}
        kind={shot?.kind || 'image'}
        hint={hint}
        onToggleHint={toggleHint}
        onClose={() => setShot(null)}
      />
    </Box>
  );
}

// Map an ApiError to a friendly, localized message (session/network aware).
function errMessage(e, t, fallbackKey) {
  return t(errKey(e, { fallback: fallbackKey }));
}
